import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import { isManagementPrimaryRole } from '../../utils/permissionStore';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  CircularProgress,
  Grid // Added Grid import
} from '@mui/material';
import { useNotification } from '../../context/NotificationContext';

function CreateUserDialog({ open, onClose, currentUser, availableRoles, regions, onUserCreated }) {
  // Use notification hook
  const { showNotification } = useNotification();
  
  // Determine if the creator is an SP
  const isCreatorSP = currentUser?.primaryRole === 'SP';
  
  // Determine if the creator is an Org 2 Admin
  const isOrg2Admin = currentUser?.organizationLevel === 2 && currentUser?.secondaryRoles?.admin === true;

  const [newUser, setNewUser] = useState({
    // Default to N/A if creator is SP or Org 2 Admin, otherwise RM
    primaryRole: (isCreatorSP || isOrg2Admin) ? 'N/A' : 'RM',
    secondaryRoles: {
      admin: false,
      dispatcher: false,
      answeringService: false,
      driver: false
    },
    isActive: true,
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US'
    },
    facilityAddress: { // Initialize facility address
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US'
    },
    // Add notification preferences, defaulting to true
    receiveEmailNotifications: true,
    receiveTextNotifications: true,
    notifyOnNewJob: true,
    notifyOnJobCanceled: true,
    notifyOnJobOnScene: true,
    notifyOnJobReassigned: true,
    notifyOnJobMarkedAsGOA: true,
    ssnLast4: '' // Add SSN (Last 4) field
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Reset form when dialog opens/closes or currentUser changes
  useEffect(() => {
    // Check if creator is SP or Org 2 Admin when resetting
    const isCreatorSP = currentUser?.primaryRole === 'SP';
    const isOrg2Admin = currentUser?.organizationLevel === 2 && currentUser?.secondaryRoles?.admin === true;
    const initialRole = (isCreatorSP || isOrg2Admin) ? 'N/A' : 'RM';
    if (open) {
      setNewUser({
        primaryRole: initialRole, // Use calculated initial role
        secondaryRoles: { admin: false, dispatcher: false, answeringService: false, driver: false },
        isActive: true,
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        username: '',
        password: '',
        confirmPassword: '',
        regions: [],
        address: { street1: '', street2: '', city: '', state: '', zip: '', country: 'US' },
        facilityAddress: { street1: '', street2: '', city: '', state: '', zip: '', country: 'US' },
        // Reset notification prefs as well
        receiveEmailNotifications: true,
        receiveTextNotifications: true,
        notifyOnNewJob: true,
        notifyOnJobCanceled: true,
        notifyOnJobOnScene: true,
        notifyOnJobReassigned: true,
        notifyOnJobMarkedAsGOA: true,
        ssnLast4: '' // Reset SSN (Last 4) field
      });
      setError(null); // Clear errors when opening
    }
  }, [open, currentUser]);

  // Helper function to get auth headers
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const handleSaveNewUser = async () => {
    // Basic frontend validation
    const basicFieldsValid = newUser.firstName && newUser.lastName && newUser.username &&
      newUser.password && newUser.email && newUser.primaryRole && newUser.phone;
    
    // Address fields are only required for SP role
    const isSpRole = newUser.primaryRole === 'SP';
    const addressFieldsValid = !isSpRole || (
      newUser.address?.street1 && newUser.address?.city &&
      newUser.address?.state && newUser.address?.zip && newUser.address?.country
    );
    
    if (!basicFieldsValid || !addressFieldsValid) {
      const missingFields = [];
      if (!basicFieldsValid) missingFields.push('basic information');
      if (isSpRole && !addressFieldsValid) missingFields.push('address information (required for SP role)');
      
      setError({ message: `Please fill in all required fields: ${missingFields.join(', ')}` });
      return;
    }
    if (newUser.password !== newUser.confirmPassword) {
      setError({ message: 'Passwords do not match' });
      return;
    }
    
    // Validate SSN (Last 4) if provided
    if (newUser.ssnLast4 && newUser.ssnLast4.trim() !== '') {
      const ssnLast4Trimmed = newUser.ssnLast4.trim();
      if (!/^\d{4}$/.test(ssnLast4Trimmed)) {
        setError({ message: 'SSN (Last 4) must be exactly 4 digits' });
        return;
      }
    }
    // Allow creating N/A role even if creator is SP
    // if (newUser.primaryRole === 'SP') { 
    //   setError({ message: 'Service Provider accounts should be created through the Submissions page' });
    //   return;
    // }

    try {
      setLoading(true);
      setError(null);

      const userData = { ...newUser };
      delete userData.confirmPassword; // Don't send confirmPassword to backend

      // Always set vendor number from current user
      if (currentUser) {
        userData.vendorNumber = currentUser.vendorNumber || currentUser.vendorId;
        userData.vendorId = currentUser.vendorNumber || currentUser.vendorId; // Backward compatibility

        // Inherit facility address if not explicitly set and current user is OW/sOW
        const canEditFacility = currentUser.primaryRole === 'OW' || currentUser.primaryRole === 'sOW';
        if (canEditFacility && (!userData.facilityAddress || !Object.values(userData.facilityAddress).some(v => v))) {
           // Check modern format first
           if (currentUser.facilityAddress && Object.values(currentUser.facilityAddress).some(v => v)) {
               userData.facilityAddress = { ...currentUser.facilityAddress };
               // Set legacy fields
               userData.facilityAddress1 = currentUser.facilityAddress.street1 || '';
               userData.facilityAddress2 = currentUser.facilityAddress.street2 || '';
               userData.facilityCity = currentUser.facilityAddress.city || '';
               userData.facilityState = currentUser.facilityAddress.state || '';
               userData.facilityZip = currentUser.facilityAddress.zip || '';
               userData.facilityCountry = currentUser.facilityAddress.country || 'US';
           }
           // Check legacy format
           else if (currentUser.facilityAddress1 || currentUser.facilityCity || currentUser.facilityState) {
               userData.facilityAddress = {
                   street1: currentUser.facilityAddress1 || '',
                   street2: currentUser.facilityAddress2 || '',
                   city: currentUser.facilityCity || '',
                   state: currentUser.facilityState || '',
                   zip: currentUser.facilityZip || '',
                   country: currentUser.facilityCountry || 'US'
               };
               // Set legacy fields
               userData.facilityAddress1 = currentUser.facilityAddress1 || '';
               userData.facilityAddress2 = currentUser.facilityAddress2 || '';
               userData.facilityCity = currentUser.facilityCity || '';
               userData.facilityState = currentUser.facilityState || '';
               userData.facilityZip = currentUser.facilityZip || '';
               userData.facilityCountry = currentUser.facilityCountry || 'US';
           }
        }
      } else {
        throw new Error('Cannot create user: Current user information is missing');
      }

      // console.log('Creating new user with data:', userData); // Removed log

      let response;
      try {
        response = await axios.post(`${process.env.REACT_APP_API_URL}/api/users`, userData, { headers: getAuthHeader() });
      } catch (primaryError) {
        // console.error('Error creating user with primary API path:', primaryError); // Removed log
        if (primaryError.response?.status === 404) {
          response = await axios.post(`${process.env.REACT_APP_API_URL}/api/user`, userData, { headers: getAuthHeader() });
        } else {
          throw primaryError; // Re-throw other errors
        }
      }

      // console.log('User created successfully:', response.data); // Removed log
      showNotification('User created successfully!', 'success');
      onUserCreated(response.data); // Pass the new user back to the parent
      onClose(); // Close the dialog on success

    } catch (error) {
      // console.error('Error creating user:', error); // Removed log
      let errorMessage = 'Failed to create user. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 400) {
         errorMessage = 'Invalid data. Check required fields or if username/email already exists.';
      }
      setError({ message: errorMessage });
      
      // Show notification for the error
      showNotification(`Error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target; // Add type and checked
    // Phone fields accept any format and normalize to (XXX) XXX-XXXX
    const nextValue = type === 'checkbox'
      ? checked
      : (name === 'phone' || name === 'phoneNumber' ? formatPhoneNumber(value) : value);
    setNewUser(prev => ({
      ...prev,
      [name]: nextValue
    }));
  };

  const handleAddressChange = (event) => {
    const { name, value } = event.target;
    setNewUser(prev => ({
      ...prev,
      address: { ...prev.address, [name]: value }
    }));
  };

  const handleFacilityAddressChange = (event) => {
    const { name, value } = event.target;
    setNewUser(prev => ({
      ...prev,
      facilityAddress: { ...prev.facilityAddress, [name]: value }
    }));
  };

  const handleSecondaryRoleChange = (event) => {
    const { name, checked } = event.target;
    setNewUser(prev => ({
      ...prev,
      secondaryRoles: { ...prev.secondaryRoles, [name]: checked }
    }));
  };

  const handleRegionChange = (event) => {
    const { value } = event.target;
    setNewUser(prev => ({
      ...prev,
      regions: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const canEditFacility = currentUser && (currentUser.primaryRole === 'OW' || currentUser.primaryRole === 'sOW');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create User</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="primary" sx={{ mb: 2 }}>
          Fields marked with * are required.
        </Typography>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error.message}
          </Typography>
        )}

        {currentUser && (
          <TextField
            margin="dense"
            label="Vendor ID (inherited)"
            fullWidth
            value={currentUser.vendorNumber || currentUser.vendorId || ''}
            disabled
            sx={{ mb: 2 }}
          />
        )}
        <TextField margin="dense" label="First Name *" name="firstName" fullWidth required value={newUser.firstName || ''} onChange={handleInputChange} />
        <TextField margin="dense" label="Last Name *" name="lastName" fullWidth required value={newUser.lastName || ''} onChange={handleInputChange} />
        <TextField
          margin="dense"
          label="SSN (Last 4)"
          name="ssnLast4"
          fullWidth
          value={newUser.ssnLast4 || ''}
          onChange={handleInputChange}
          inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
          helperText="Optional - Enter exactly 4 digits for de-duplication purposes"
        />
        <TextField margin="dense" label="Phone *" name="phone" fullWidth required value={newUser.phone || ''} onChange={handleInputChange} />
        <TextField margin="dense" label="Email *" name="email" type="email" fullWidth required value={newUser.email || ''} onChange={handleInputChange} />
        <TextField margin="dense" label="Username *" name="username" fullWidth required value={newUser.username || ''} onChange={handleInputChange} />
        <TextField margin="dense" label="Password *" name="password" type="password" fullWidth required value={newUser.password || ''} onChange={handleInputChange} />
        <TextField margin="dense" label="Confirm Password *" name="confirmPassword" type="password" fullWidth required value={newUser.confirmPassword || ''} onChange={handleInputChange} />

        {/* Conditionally render Role selection only if creator is NOT SP and NOT Org 2 Admin */}
        {!isCreatorSP && !isOrg2Admin && (
          <FormControl fullWidth margin="dense" required>
            <InputLabel>Primary Role *</InputLabel>
            <Select name="primaryRole" value={newUser.primaryRole || ''} onChange={handleInputChange}>
              {availableRoles.length > 0 ? (
                availableRoles.map((role) => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>Loading roles...</MenuItem>
              )}
            </Select>
          </FormControl>
        )}
        
        {/* If creator is SP or Org 2 Admin, role is forced to N/A and not shown */}

        <Typography variant="subtitle1" sx={{ mt: 2 }}>Secondary Roles</Typography>
        <FormControlLabel control={<Checkbox name="admin" checked={newUser.secondaryRoles?.admin || false} onChange={handleSecondaryRoleChange} />} label="Admin" />
        <FormControlLabel control={<Checkbox name="dispatcher" checked={newUser.secondaryRoles?.dispatcher || false} onChange={handleSecondaryRoleChange} />} label="Dispatcher" />
        {isManagementPrimaryRole(newUser.primaryRole) ? (
          <FormControlLabel control={<Checkbox name="driver" checked disabled />} label="Driver (always on for management roles)" />
        ) : (
          <FormControlLabel control={<Checkbox name="driver" checked={newUser.secondaryRoles?.driver || false} onChange={handleSecondaryRoleChange} />} label="Driver" />
        )}
        <FormControlLabel control={<Checkbox name="answeringService" checked={newUser.secondaryRoles?.answeringService || false} onChange={handleSecondaryRoleChange} />} label="Answering Service" />

        {/* Conditionally render Region Assignment only if creator is NOT SP, NOT Org 2 Admin, and selected role is RM */}
        {!isCreatorSP && !isOrg2Admin && newUser.primaryRole === 'RM' && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>Region Assignment</Typography>
            <FormControl fullWidth margin="dense">
              <InputLabel>Assigned Region(s)</InputLabel>
              <Select
                multiple
                name="regions"
                value={newUser.regions || []}
                onChange={handleRegionChange}
                renderValue={(selected) => {
                  const selectedRegions = regions.filter(r => selected.includes(r._id));
                  return selectedRegions.map(r => r.name).join(', ');
                }}
              >
                {regions.map((region) => (
                  <MenuItem key={region._id} value={region._id}>{region.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Personal Address {newUser.primaryRole === 'SP' ? '*' : '(Optional)'}
        </Typography>
        <TextField
          margin="dense"
          label={`Street 1 ${newUser.primaryRole === 'SP' ? '*' : ''}`}
          name="street1"
          fullWidth
          required={newUser.primaryRole === 'SP'}
          value={newUser.address?.street1 || ''}
          onChange={handleAddressChange}
        />
        <TextField margin="dense" label="Street 2" name="street2" fullWidth value={newUser.address?.street2 || ''} onChange={handleAddressChange} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            margin="dense"
            label={`City ${newUser.primaryRole === 'SP' ? '*' : ''}`}
            name="city"
            fullWidth
            required={newUser.primaryRole === 'SP'}
            value={newUser.address?.city || ''}
            onChange={handleAddressChange}
          />
          <TextField
            margin="dense"
            label={`State ${newUser.primaryRole === 'SP' ? '*' : ''}`}
            name="state"
            fullWidth
            required={newUser.primaryRole === 'SP'}
            value={newUser.address?.state || ''}
            onChange={handleAddressChange}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            margin="dense"
            label={`ZIP ${newUser.primaryRole === 'SP' ? '*' : ''}`}
            name="zip"
            fullWidth
            required={newUser.primaryRole === 'SP'}
            value={newUser.address?.zip || ''}
            onChange={handleAddressChange}
          />
          <FormControl fullWidth margin="dense" required={newUser.primaryRole === 'SP'}>
            <InputLabel>{`Country ${newUser.primaryRole === 'SP' ? '*' : ''}`}</InputLabel>
            <Select name="country" value={newUser.address?.country || 'US'} onChange={handleAddressChange}>
              <MenuItem value="US">United States</MenuItem>
              <MenuItem value="CA">Canada</MenuItem>
              <MenuItem value="MX">Mexico</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {canEditFacility && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>Facility Address (Optional - Inherited if blank)</Typography>
            <TextField margin="dense" label="Street 1" name="street1" fullWidth value={newUser.facilityAddress?.street1 || ''} onChange={handleFacilityAddressChange} />
            <TextField margin="dense" label="Street 2" name="street2" fullWidth value={newUser.facilityAddress?.street2 || ''} onChange={handleFacilityAddressChange} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField margin="dense" label="City" name="city" fullWidth value={newUser.facilityAddress?.city || ''} onChange={handleFacilityAddressChange} />
              <TextField margin="dense" label="State" name="state" fullWidth value={newUser.facilityAddress?.state || ''} onChange={handleFacilityAddressChange} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField margin="dense" label="ZIP" name="zip" fullWidth value={newUser.facilityAddress?.zip || ''} onChange={handleFacilityAddressChange} />
              <FormControl fullWidth margin="dense">
                <InputLabel>Country</InputLabel>
                <Select name="country" value={newUser.facilityAddress?.country || 'US'} onChange={handleFacilityAddressChange}>
                  <MenuItem value="US">United States</MenuItem>
                  <MenuItem value="CA">Canada</MenuItem>
                  <MenuItem value="MX">Mexico</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        {/* Notification Preferences Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>Notification Preferences</Typography>
        <Grid container spacing={1} sx={{ ml: 1 }}> {/* Use Grid container */}
            <Grid item xs={12} sm={6}> {/* Grid item for first column */}
                <FormControlLabel control={<Checkbox name="receiveEmailNotifications" checked={newUser.receiveEmailNotifications} onChange={handleInputChange} />} label="Receive General Email Notifications" />
            </Grid>
            <Grid item xs={12} sm={6}> {/* Grid item for second column */}
                <FormControlLabel control={<Checkbox name="receiveTextNotifications" checked={newUser.receiveTextNotifications} onChange={handleInputChange} />} label="Receive General Text Notifications" />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControlLabel control={<Checkbox name="notifyOnNewJob" checked={newUser.notifyOnNewJob} onChange={handleInputChange} />} label="Notify on New Job" />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControlLabel control={<Checkbox name="notifyOnJobCanceled" checked={newUser.notifyOnJobCanceled} onChange={handleInputChange} />} label="Notify on Job Canceled" />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControlLabel control={<Checkbox name="notifyOnJobOnScene" checked={newUser.notifyOnJobOnScene} onChange={handleInputChange} />} label="Notify on Job On Scene" />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControlLabel control={<Checkbox name="notifyOnJobReassigned" checked={newUser.notifyOnJobReassigned} onChange={handleInputChange} />} label="Notify on Job Reassigned" />
            </Grid>
            <Grid item xs={12} sm={6}>
                <FormControlLabel control={<Checkbox name="notifyOnJobMarkedAsGOA" checked={newUser.notifyOnJobMarkedAsGOA} onChange={handleInputChange} />} label="Notify on Job Marked GOA" />
            </Grid>
        </Grid>

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSaveNewUser} color="primary" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreateUserDialog;
